/**
 * 全局契约层。
 *
 * 这个文件里的每个类型都是前后端的"接口合同"。改任何一个字段，
 * 前端和后端会同时报编译错误 —— 这就是本项目可维护性的技术底座。
 *
 * 三条数据主干：
 *   1. IntakeDraft  录入：三种适配器（手工/拍照/语音）的统一输出
 *   2. MealBrief    点菜：每次开火前的轻量偏好
 *   3. Recipe       结果：AI 依据「冰箱现状 + 硬约束偏好 + MealBrief」生成
 */

/* ------------------------------------------------------------------ */
/* 基础枚举                                                            */
/* ------------------------------------------------------------------ */

/** 食材品类。用于分类展示、默认单位推断、菜谱生成时的约束。 */
export const FOOD_CATEGORIES = [
  '蔬菜',
  '水果',
  '肉类',
  '水产',
  '蛋奶',
  '豆制品',
  '主食',
  '调料',
  '干货',
  '冷冻',
  '熟食',
  '其他',
] as const;

export type FoodCategory = (typeof FOOD_CATEGORIES)[number];

/** 录入来源。手工是零依赖兜底路径，另两条依赖 AI。 */
export const INTAKE_SOURCES = ['manual', 'receipt', 'voice'] as const;

export type IntakeSource = (typeof INTAKE_SOURCES)[number];

/* ------------------------------------------------------------------ */
/* 契约 1：IntakeDraft —— 录入适配器的统一输出                          */
/* ------------------------------------------------------------------ */

/**
 * 一条待入库的食材。
 * 注意 `confidence` 是必须字段：手工输入恒为 1，AI 识别输出真实置信度。
 * 前端据此决定是否高亮提示用户复核。
 */
export interface IntakeItem {
  /** 食材名，已归一化（去空格、去量词、统一别名） */
  name: string;
  /** 数量。AI 说不清时为 null，由用户在复核界面补 */
  qty: number | null;
  /** 单位。null 表示用该品类的默认单位 */
  unit: string | null;
  /** 品类。null 表示由字典推断 */
  category: FoodCategory | null;
  /**
   * 已经放了几天。0 表示今天才放进来。
   * 用户自己说了算，不由系统猜保质期。
   */
  storedDays: number;
  /** 0..1。低于 REVIEW_THRESHOLD 时前端必须显眼提示 */
  confidence: number;
  /** 原始文本片段，让用户能对照原文核对（如小票上的"精品五花肉 500g"） */
  raw: string | null;
}

/**
 * 三种录入适配器（manual / receipt / voice）的唯一输出类型。
 *
 * 下游（草稿复核 → 冰箱仓库）对这个 draft 从哪来**零感知**：
 * 也正因如此，AI 链路可以随时拔掉而不影响产品可用性。
 */
export interface IntakeDraft {
  /** 客户端生成的草稿 id，用于幂等提交 */
  clientDraftId: string;
  source: IntakeSource;
  items: IntakeItem[];
  /** 需要用户注意的问题，例如"识别到 2 项置信度偏低" */
  warnings: string[];
  /** 语音转写原文 / 小票 OCR 原文，保留以便用户核对 */
  transcript: string | null;
  /** 若本条草稿是由其他来源降级而来（如语音识别失败回落手工），记录来源 */
  fallbackFrom: IntakeSource | null;
  createdAt: string;
}

/** 置信度低于此值时，复盘界面必须强制提示用户核对。 */
export const REVIEW_THRESHOLD = 0.75;

/* ------------------------------------------------------------------ */
/* 契约 2：硬约束偏好档案（DietProfile）                                */
/* ------------------------------------------------------------------ */

export const DIET_GOALS = ['balanced', 'fat-loss', 'high-protein', 'whatever'] as const;
export type DietGoal = (typeof DIET_GOALS)[number];

export const DIET_GOAL_LABELS: Record<DietGoal, string> = {
  balanced: '均衡家常',
  'fat-loss': '减脂低油',
  'high-protein': '高蛋白',
  whatever: '别管那么多',
};

export const SPICY_LEVELS = ['none', 'mild', 'medium', 'hot'] as const;
export type SpicyLevel = (typeof SPICY_LEVELS)[number];

export const SPICY_LEVEL_LABELS: Record<SpicyLevel, string> = {
  none: '不吃辣',
  mild: '微辣',
  medium: '中辣',
  hot: '重辣',
};

/**
 * 硬约束偏好档案。
 *
 * 这是**永久生效**的约束，刻意不放进每次的"点菜"流程里 ——
 * 忌口和过敏不能每顿饭靠现场回忆，漏一次就是事故。
 */
export interface DietProfile {
  /** 健康导向，可多选 */
  goals: DietGoal[];
  /** 忌口与过敏。这是硬约束，AI 生成时必须无条件排除 */
  avoid: string[];
  spicy: SpicyLevel;
  /** 单次做饭可接受的最长耗时（分钟）。null 表示不限 */
  maxMinutes: number | null;
  updatedAt: string;
}

export const DEFAULT_DIET_PROFILE: Omit<DietProfile, 'updatedAt'> = {
  goals: ['balanced'],
  avoid: [],
  spicy: 'mild',
  maxMinutes: 40,
};

/* ------------------------------------------------------------------ */
/* 契约 3：MealBrief —— 每次开火的"点菜"                                */
/* ------------------------------------------------------------------ */

export const FLAVOR_PREFERENCES = ['light', 'rich', 'spicy', 'whatever'] as const;
export type FlavorPreference = (typeof FLAVOR_PREFERENCES)[number];

export const FLAVOR_LABELS: Record<FlavorPreference, string> = {
  light: '清淡',
  rich: '重口解馋',
  spicy: '要辣',
  whatever: '随便',
};

export const PROTEIN_PREFERENCES = ['meat', 'veg', 'either'] as const;
export type ProteinPreference = (typeof PROTEIN_PREFERENCES)[number];

export const PROTEIN_LABELS: Record<ProteinPreference, string> = {
  meat: '想吃肉',
  veg: '素一点',
  either: '都行',
};

/**
 * 每次开火前的轻量点菜结果。两三个 chip 就点完，可整体跳过。
 *
 * 默认值应当由前端用"上一次的 MealBrief"预填，所以熟手基本一点就走。
 */
export interface MealBrief {
  flavor: FlavorPreference;
  protein: ProteinPreference;
  /** 今天愿意花的时间。null 表示不限 */
  minutes: number | null;
  /** 用户点名要消耗掉的冰箱项 id（通常是放得最久的），会作为强约束注入 prompt */
  useUpItemIds: string[];
  /** 自由补充，例如"不想开火"、"想喝汤" */
  note: string;
}

export const DEFAULT_MEAL_BRIEF: MealBrief = {
  flavor: 'whatever',
  protein: 'either',
  minutes: null,
  useUpItemIds: [],
  note: '',
};

/* ------------------------------------------------------------------ */
/* 契约 4：冰箱项                                                       */
/* ------------------------------------------------------------------ */

export interface FridgeItem {
  id: string;
  name: string;
  qty: number;
  unit: string;
  category: FoodCategory;
  /**
   * 开始存放的日期（ISO 日期 YYYY-MM-DD）。
   * 存的是锚点而不是天数本身 —— 存一个数字的话，它永远停在录入那天。
   * 界面只呈现"已放几天"，用 storage.ts 的 daysStored() 实时算。
   */
  storedAt: string;
  source: IntakeSource;
  addedAt: string;
  updatedAt: string;
}

/* ------------------------------------------------------------------ */
/* 契约 5：菜谱                                                        */
/* ------------------------------------------------------------------ */

export interface RecipeIngredient {
  name: string;
  /** 人类可读用量，例如 "约 300g"、"2 个" */
  amount: string;
  /** 冰箱里是否已有 */
  have: boolean;
  /** 对应冰箱项 id，用于"这道菜消耗了哪些库存" */
  fridgeItemId: string | null;
}

export interface RecipeStep {
  order: number;
  text: string;
  /** 该步骤预计耗时（分钟） */
  minutes: number | null;
}

export interface Recipe {
  id: string;
  title: string;
  /** 一句话说明，例如"用掉快蔫的菠菜，少油快炒" */
  summary: string;
  /** 总耗时（分钟） */
  minutes: number;
  servings: number;
  /** 健康说明。由 AI 结合 DietProfile 生成 */
  healthNote: string;
  flavorTags: string[];
  ingredients: RecipeIngredient[];
  steps: RecipeStep[];
  /** 生成时消耗的冰箱项 id（仅记录意图，不自动扣库存） */
  consumedItemIds: string[];
  /** 生成时的点菜快照，便于"再做一次" */
  brief: MealBrief | null;
  favorite: boolean;
  createdAt: string;
}

/* ------------------------------------------------------------------ */
/* 契约 6：待买清单                                                     */
/* ------------------------------------------------------------------ */

export interface BuyItem {
  id: string;
  name: string;
  amount: string | null;
  checked: boolean;
  /** 来自哪道菜，便于"这道菜的料我一次性买齐" */
  sourceRecipeId: string | null;
  createdAt: string;
}

/* ------------------------------------------------------------------ */
/* 契约 7：冰箱（household）—— 多租户的边界                             */
/* ------------------------------------------------------------------ */

/**
 * 一个冰箱 = 一个独立的数据库分区。
 *
 * 说人话：名字叫"冰箱"的这个东西，在系统里就是**隔离的边界**。
 * 张家的冰箱和李家的冰箱之间，数据在 SQL 层面就不互通，
 * 而不是靠应用层"记得加 where"。所有仓库查询都必须带 household_id，
 * 这一条是整个多租户设计的唯一硬性纪律。
 */
export interface Household {
  id: string;
  name: string;
  /** 当前这台设备在这个冰箱里的角色 */
  role: HouseholdRole;
  /** 有多少台设备在用这个冰箱 */
  memberCount: number;
  createdAt: string;
}

export const HOUSEHOLD_ROLES = ['owner', 'member'] as const;
export type HouseholdRole = (typeof HOUSEHOLD_ROLES)[number];

/** 冰箱名长度上限。太长会把切换器撑爆，也没必要 */
export const HOUSEHOLD_NAME_MAX = 24;

/**
 * 邀请码：6 位纯数字，给人念、给人发微信用的。
 *
 * 为什么不让它当长期钥匙 —— 6 位数字只有 100 万种组合，
 * 挂公网上是能被枚举的。所以它只是个**一次性门票**：
 * 用过就作废，没用过 24 小时后也作废。
 * 真正长期用的是兑换来的设备凭证（32 位，用户看不到）。
 */
export const INVITE_CODE_LENGTH = 6;

/** 邀请码的兜底有效期（小时）。用户忘了用也不会一直敞着 */
export const INVITE_CODE_TTL_HOURS = 24;

/** 一个仍然有效的邀请码 */
export interface InviteCode {
  code: string;
  expiresAt: string;
  createdAt: string;
}

/** 一台已经进入这个冰箱的设备。用于"谁在用"的管理与踢人 */
export interface DeviceEntry {
  id: string;
  /** 设备自己填的备注，例如"老婆的 iPhone" */
  label: string;
  createdAt: string;
  lastSeenAt: string;
  /** 是不是当前这台设备。自己不能踢自己，界面要区分 */
  current: boolean;
}

/* ---- 传输对象 ---- */

export interface CreateHouseholdRequest {
  name: string;
  /** 设备备注，纯展示用，可空 */
  deviceLabel?: string;
}

export interface JoinHouseholdRequest {
  code: string;
  deviceLabel?: string;
}

/**
 * 创建 / 加入成功后的返回。
 * `householdKey` 是设备凭证 —— 只在这一次响应里出现一次，
 * 之后由客户端自己存着。服务端只存它的哈希，丢了就重新用邀请码进来。
 */
export interface HouseholdSession {
  household: Household;
  householdKey: string;
}

export interface HouseholdDetail {
  household: Household;
  /** 当前有效的邀请码，没有则为 null（用量用完了或过期了） */
  invite: InviteCode | null;
}

/* ------------------------------------------------------------------ */
/* API 传输对象                                                        */
/* ------------------------------------------------------------------ */

export interface ApiErrorBody {
  error: string;
  message: string;
}

export interface ParseTextRequest {
  /** 语音转写后的自由文本，例如"鸡蛋还有六个，西红柿两个，猪肉半斤" */
  text: string;
}

export interface ParseImageRequest {
  /** data URL（image/jpeg;base64,...）或裸 base64 */
  image: string;
}

export interface GenerateRecipeRequest {
  brief: MealBrief;
  /** 本次想生成几道，默认 3 */
  count?: number;
}

export interface GenerateRecipeResponse {
  recipes: Recipe[];
  /** 本次注入 prompt 的上下文摘要，便于排查"为什么给我推这道菜" */
  contextSummary: string;
}

export interface TranscribeResponse {
  text: string;
  engine: string;
}
